/* The device store. Dexie over IndexedDB.
 *
 * This is the app's source of truth. The UI subscribes here and never to the
 * network. Every write goes through commit.ts — nothing else may touch these
 * tables directly.
 */

import Dexie, { type Table } from 'dexie';
import type {
  Comment,
  User,
  CorrectionLog,
  Evidence,
  FieldAssignment,
  PriorityItem,
  PriorityRanking,
  PriorityRectified,
  StatusLog,
} from '@kilroy/shared';

export interface OutboxEntry {
  /** Dexie auto-increment. This IS client_seq in the sync contract, which is
   *  why it must never be reused or reordered. */
  seq?: number;
  kind: string;
  ref_id: string;
  attempts: number;
  next_attempt_at: number;
  created_at: string;
  last_error?: string;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

/** An in-progress capture. Written on every field change so process death
 *  costs nothing. Not synced — a draft is not evidence. */
export interface Draft {
  id: string;
  updated_at: string;
  data: unknown;
}

export class KilroyDB extends Dexie {
  evidence!: Table<Evidence, string>;
  corrections!: Table<CorrectionLog, string>;
  statuses!: Table<StatusLog, string>;
  assignments!: Table<FieldAssignment, string>;
  comments!: Table<Comment, string>;
  priorityItems!: Table<PriorityItem, string>;
  priorityRankings!: Table<PriorityRanking, string>;
  priorityRectified!: Table<PriorityRectified, string>;
  outbox!: Table<OutboxEntry, number>;
  users!: Table<User, string>;
  meta!: Table<MetaRow, string>;
  drafts!: Table<Draft, string>;

  constructor() {
    super('kilroy-field');
    this.version(1).stores({
      evidence:
        'id, captured_at, evidence_type, sync_state, source_assignment_id, property_id, user_id',
      corrections: 'id, evidence_id, corrected_at, sync_state',
      statuses: 'id, [entity_type+entity_id], changed_at, sync_state',
      assignments: 'id, assigned_to, current_status, property_id, updated_at',
      comments: 'id, [entity_type+entity_id], created_at',
      priorityItems: 'id, property_id, status',
      priorityRankings: 'id, item_id, user_id, ranked_at, sync_state',
      priorityRectified: 'id, property_id, rectified_at, sync_state',
      outbox: '++seq, kind, ref_id, next_attempt_at',
      users: 'id',
      meta: 'key',
      drafts: 'id, updated_at',
    });
  }
}

export const db = new KilroyDB();

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row === undefined ? fallback : (row.value as T);
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}
